import React, { useState } from "react";
import {
	AppBar,
	Toolbar,
	IconButton,
	Typography,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
	Fab,
	Divider,
	Button,
} from "@mui/material";
import {
	Add as AddIcon,
	MoreVert as MoreIcon,
	Delete as DeleteIcon,
	Download as DownloadIcon,
	Publish as PublishIcon,
	Map as MapIcon,
	SelectAll as SelectAllIcon,
	Warning as WarningIcon,
	ContentCopy as DuplicateIcon,
	Merge as MergeIcon,
	CallSplit as SplitIcon,
} from "@mui/icons-material";

/**
 * Props for the mobile toolbar component
 */
export interface ImportTableToolbarMobileProps {
	jobCount: number;
	selectedCount: number;
	hasValidationErrors: boolean;
	onAddJob: () => void;
	onDeleteSelected: () => void;
	onDuplicateSelected: () => void;
	onMergeSelected: () => void;
	onSplitSelected?: () => void;
	onSelectAll: () => void;
	onCreateJobs: () => Promise<void>;
	onExportCSV: () => void;
	mapDisplayMode?: "modal" | "drawer" | null;
	setMapDisplayMode?: (mode: "modal" | "drawer" | null) => void;
	/** For split feature - IDs of features selected via lasso on map */
	selectedFeatureIds?: Set<string>;
	/** For split feature - ID of the single selected job */
	selectedJobId?: string;
}

/**
 * Mobile toolbar for ImportTable.
 * Features:
 * - Sticky AppBar at top
 * - Overflow menu for less-frequent actions
 * - FAB for adding new jobs
 * - Selection mode indicator
 */
export const ImportTableToolbarMobile: React.FC<ImportTableToolbarMobileProps> = ({
	jobCount,
	selectedCount,
	hasValidationErrors,
	onAddJob,
	onDeleteSelected,
	onDuplicateSelected,
	onMergeSelected,
	onSplitSelected,
	onSelectAll,
	onCreateJobs,
	onExportCSV,
	setMapDisplayMode,
	selectedFeatureIds,
}) => {
	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const menuOpen = Boolean(menuAnchor);

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
		setMenuAnchor(event.currentTarget);
	};

	const handleMenuClose = () => {
		setMenuAnchor(null);
	};

	const handleCreateJobs = async () => {
		handleMenuClose();
		setIsSubmitting(true);
		try {
			await onCreateJobs();
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleExportCSV = () => {
		handleMenuClose();
		onExportCSV();
	};

	const handleShowMap = () => {
		handleMenuClose();
		setMapDisplayMode?.("modal");
	};

	const handleDeleteSelected = () => {
		handleMenuClose();
		onDeleteSelected();
	};

	const handleDuplicateSelected = () => {
		handleMenuClose();
		onDuplicateSelected();
	};

	const handleMergeSelected = () => {
		handleMenuClose();
		onMergeSelected();
	};

	const handleSplitSelected = () => {
		handleMenuClose();
		onSplitSelected?.();
	};

	const isSelectionMode = selectedCount > 0;
	const canMerge = selectedCount >= 2;
	const canSplit = selectedCount === 1 && selectedFeatureIds && selectedFeatureIds.size > 0;

	return (
		<>
			<AppBar position="sticky" color={isSelectionMode ? "primary" : "default"} elevation={1} sx={{ top: 0 }}>
				<Toolbar>
					{/* Title / Selection Count */}
					<Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
						{isSelectionMode ? `${selectedCount} selected` : `Jobs (${jobCount})`}
					</Typography>

					{/* Submit Button with validation indicator */}
					<Button
						variant={isSelectionMode ? "outlined" : "contained"}
						color={hasValidationErrors ? "warning" : "primary"}
						onClick={handleCreateJobs}
						disabled={isSubmitting || jobCount === 0}
						startIcon={hasValidationErrors ? <WarningIcon /> : <PublishIcon />}
						sx={{
							ml: 1,
							minWidth: "auto",
							px: 2,
							...(isSelectionMode && { color: "inherit", borderColor: "inherit" }),
						}}
					>
						{isSubmitting ? "..." : "Submit"}
					</Button>

					{/* Overflow Menu */}
					<IconButton color="inherit" onClick={handleMenuOpen} aria-label="more options" sx={{ ml: 1 }}>
						<MoreIcon />
					</IconButton>
				</Toolbar>
			</AppBar>

			{/* Overflow Menu */}
			<Menu
				anchorEl={menuAnchor}
				open={menuOpen}
				onClose={handleMenuClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
			>
				<MenuItem onClick={handleExportCSV}>
					<ListItemIcon>
						<DownloadIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText>Export CSV</ListItemText>
				</MenuItem>
				{setMapDisplayMode && (
					<MenuItem onClick={handleShowMap}>
						<ListItemIcon>
							<MapIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText>View Map</ListItemText>
					</MenuItem>
				)}
				<Divider />
				<MenuItem onClick={onSelectAll}>
					<ListItemIcon>
						<SelectAllIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText>{selectedCount === jobCount ? "Deselect All" : "Select All"}</ListItemText>
				</MenuItem>
				{selectedCount > 0 && (
					<>
						<MenuItem onClick={handleDuplicateSelected}>
							<ListItemIcon>
								<DuplicateIcon fontSize="small" />
							</ListItemIcon>
							<ListItemText>Duplicate Selected ({selectedCount})</ListItemText>
						</MenuItem>
						{canMerge && (
							<MenuItem onClick={handleMergeSelected}>
								<ListItemIcon>
									<MergeIcon fontSize="small" />
								</ListItemIcon>
								<ListItemText>Merge Selected ({selectedCount})</ListItemText>
							</MenuItem>
						)}
						{canSplit && onSplitSelected && (
							<MenuItem onClick={handleSplitSelected}>
								<ListItemIcon>
									<SplitIcon fontSize="small" />
								</ListItemIcon>
								<ListItemText>Split by Features ({selectedFeatureIds?.size})</ListItemText>
							</MenuItem>
						)}
						<Divider />
						<MenuItem onClick={handleDeleteSelected}>
							<ListItemIcon>
								<DeleteIcon fontSize="small" color="error" />
							</ListItemIcon>
							<ListItemText primaryTypographyProps={{ color: "error" }}>Delete Selected ({selectedCount})</ListItemText>
						</MenuItem>
					</>
				)}
			</Menu>

			{/* Floating Action Button for Add Job */}
			<Fab
				color="primary"
				aria-label="add job"
				onClick={onAddJob}
				sx={{
					position: "fixed",
					bottom: 16,
					right: 16,
					zIndex: 1000,
				}}
			>
				<AddIcon />
			</Fab>
		</>
	);
};
